import { useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, Collapse, Dialog, DialogContent, IconButton, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { baseUrl, assetFullLabel } from '../shared';

const fmtDate = (d) => (d ? d.split('-').reverse().join('/') : '-');

function PartRow({ part }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TableRow hover onClick={() => setOpen(!open)} sx={{ cursor: 'pointer' }}>
        <TableCell sx={{ width: 40, py: 0.5 }}>
          <IconButton size="small" aria-label={open ? 'Hide timesheet breakdown' : 'Show timesheet breakdown'}>
            {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell align="left" className="aims-mono">{part.partNo}</TableCell>
        <TableCell align="center" className="aims-mono">{part.totalQty}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={3} sx={{ py: 0, borderBottom: open ? undefined : 'none', background: 'var(--surface-2)' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            {/* Stacked rows instead of a nested table: a 4-column table overflows the dialog on mobile. */}
            <Box sx={{ my: 1, display: 'flex', flexDirection: 'column' }}>
              {part.timesheets.map((ts, i) => (
                <Box
                  key={ts.timesheetId + '-' + i}
                  sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1.5,
                    py: 0.5,
                    borderBottom: i < part.timesheets.length - 1 ? '1px dashed var(--border)' : 'none',
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" className="aims-mono">{fmtDate(ts.workPerformedOn)}</Typography>
                    <Typography variant="caption" sx={{ color: 'var(--muted)', display: 'block', overflowWrap: 'anywhere' }}>
                      {ts.username} · Job {ts.jobNumber}
                    </Typography>
                  </Box>
                  <Typography variant="body2" className="aims-mono" sx={{ flexShrink: 0 }}>× {ts.qty}</Typography>
                </Box>
              ))}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function PartsUsedModal({ open, onClose, asset }) {
  const [parts, setParts] = useState(null);
  const [error, setError] = useState(false);
  const catNo = asset ? asset.catNo : null;

  useEffect(() => {
    if (!open || !catNo) return;
    setParts(null);
    setError(false);
    fetch(baseUrl + 'timesheet/partsUsed', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'username': localStorage.getItem('username'),
        'bToken': localStorage.getItem('bToken')
      },
      body: JSON.stringify({ catNo: catNo })
    })
      .then((res) => res.json())
      .then((json) => setParts(Array.isArray(json) ? json : []))
      .catch(() => { setParts([]); setError(true); });
  }, [open, catNo]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: '82vh' }}>
          {/* Pinned header */}
          <Box sx={{ flexShrink: 0, px: 3, py: 1.5, borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Parts Used</Typography>
            <Typography variant="caption" className="aims-mono" sx={{ color: 'var(--muted)' }}>{assetFullLabel(asset)}</Typography>
          </Box>
          {/* Scrollable middle */}
          <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 2, md: 3 }, py: 2 }}>
            {parts === null ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid var(--border)', borderRadius: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 40 }} />
                        <TableCell align="left" sx={{ fontWeight: 600 }}>Item Code</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>Total Qty</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parts.length > 0 ? parts.map((part) => (
                        <PartRow key={part.partNo} part={part} />
                      )) : (
                        <TableRow>
                          <TableCell colSpan={3} align="center" sx={{ color: 'var(--muted)', py: 3 }}>
                            {error ? 'Could not load parts for this asset. Please try again.' : 'No parts have been used on this asset yet.'}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {parts.length > 0 ? (
                  <Typography variant="caption" sx={{ color: 'var(--muted)', display: 'block', mt: 1 }}>
                    Tap an item to see its per-timesheet breakdown.
                  </Typography>
                ) : null}
              </>
            )}
          </Box>
          {/* Pinned footer */}
          <Box sx={{ flexShrink: 0, display: 'flex', justifyContent: 'flex-end', px: 3, py: 1.5, borderTop: '1px solid var(--border)', background: 'var(--surface)' }}>
            <Button variant="outlined" color="inherit" sx={{ color: 'var(--muted)', borderColor: 'var(--border-strong)' }} onClick={onClose}>Close</Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
